import React, { useState } from "react";
import {
  Button,
  Form,
  List,
  Accordion,
  Icon,
  Modal,
  Confirm,
} from "semantic-ui-react";
import { useMutation } from "@apollo/client";
import { EDIT_FIELD, DELETE_FIELD, getFieldInput } from "../GraphQL";
import { v4 as uuidv4 } from "uuid";
import isEqual from "lodash/isEqual";
import gql from "graphql-tag";

const FIELD_OPTIONS = [
  { text: "Text Input", value: "Input" },
  { text: "Checkboxes", value: "Checkbox" },
  { text: "Radio Buttons", value: "Radio" },
  { text: "Text Area", value: "TextArea" },
  { text: "Dropdown Selection", value: "Select" },
];

const SHOW_OPTIONS = {
  Input: false,
  TextArea: false,
  Select: true,
  Checkbox: true,
  Radio: true,
};

const EditField = ({
  initialField,
  onDeleted,
  onSaveCompleted,
  onCanceled,
}) => {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [field, setField] = useState(initialField);
  const [deleteFieldConfirmOpen, setDeleteFieldConfirmOpen] = useState(false);
  const [fieldDeleteError, setFieldDeleteError] = useState();
  const [editField] = useMutation(EDIT_FIELD);
  const [deleteField] = useMutation(DELETE_FIELD);

  const handleChange = (e, { name, value }) => {
    setField({
      ...field,
      [name]: value,
    });
  };

  const removeOption = (index) => {
    const options = field.options.slice();
    options.splice(index, 1);

    setField({
      ...field,
      options,
    });
  };

  const handleOptionChange = (index, { name, value }) => {
    const options = field.options.slice();
    options[index] = { ...options[index], text: value };

    setField({
      ...field,
      options,
    });
  };

  const addOption = () => {
    var options = field.options.slice();
    options.push({
      text: "",
    });

    setField({
      ...field,
      options,
    });
  };

  const cancelField = () => {
    initialField.isNew ? onDeleted(field.id) : setField(initialField);
    onCanceled();
  };

  const isClean = isEqual(field, initialField);

  return (
    <List.Item>
      <List.Content>
        <Form.Input
          label="Question"
          placeholder="New Question"
          name="label"
          value={field.label}
          onChange={handleChange}
        />
        <Form.Select
          label="Type"
          options={FIELD_OPTIONS}
          name="control"
          value={field.control}
          onChange={handleChange}
        />
        {SHOW_OPTIONS[field.control] ? (
          <blockquote>
            {field.options
              ? field.options.map((option, i) => (
                  <Form.Input
                    key={`option${i}`}
                    value={option.text}
                    onChange={(e, args) => handleOptionChange(i, args)}
                    action={{
                      color: "red",
                      icon: "remove",
                      onClick: () => removeOption(i),
                    }}
                  />
                ))
              : null}
            <Form.Button onClick={addOption}>Add Option</Form.Button>
          </blockquote>
        ) : null}
        <Form.Group>
          <Form.Button
            basic
            primary
            type="button"
            disabled={isClean && !field.isNew}
            loading={saving}
            onClick={() => {
              setSaving(true);
              const { isNew, ...fieldToSave } = field;
              editField({
                variables: { field: getFieldInput(fieldToSave) },
                onCompleted: () => {
                  setSaving(false);
                  setField(fieldToSave);
                  onSaveCompleted(field);
                },
                update: (cache, { data: { editField } }) => {
                  cache.modify({
                    fields: {
                      myFields(existingFieldRefs = [], { readField }) {
                        const newFieldRef = cache.writeFragment({
                          data: editField,
                          fragment: gql`
                            fragment NewField on Field {
                              id
                            }
                          `,
                        });
                        if (
                          existingFieldRefs.some(
                            (ref) => readField("id", ref) === editField.id
                          )
                        ) {
                          return existingFieldRefs;
                        }
                        return [...existingFieldRefs, newFieldRef];
                      },
                    },
                  });
                },
              });
            }}
          >
            Save
          </Form.Button>
          <Form.Button
            basic
            disabled={isClean && !field.isNew}
            type="button"
            onClick={cancelField}
          >
            Cancel
          </Form.Button>
          <Form.Button
            basic
            loading={deleting}
            disabled={field.isNew}
            color="red"
            type="button"
            onClick={() => setDeleteFieldConfirmOpen(true)}
          >
            Delete Question
          </Form.Button>
        </Form.Group>
      </List.Content>
      <Confirm
        open={deleteFieldConfirmOpen}
        content="Are you sure you want to delete this question from your Question Bank?"
        size="tiny"
        onCancel={() => setDeleteFieldConfirmOpen(false)}
        onConfirm={() => {
          setDeleteFieldConfirmOpen(false);
          setDeleting(true);
          deleteField({
            variables: { id: field.id },
            onCompleted: () => {
              onDeleted(field.id);
            },
            onError: (e) => {
              setFieldDeleteError(
                JSON.parse(e.graphQLErrors[0].message).map((s) => s.name)
              );
              setDeleting(false);
            },
            update: (cache, { data: { deleteField } }) => {
              console.log("update", deleteField);
              cache.modify({
                fields: {
                  myFields(existingFieldRefs = [], { readField }) {
                    existingFieldRefs.filter(
                      (r) => readField("id", r) !== field.id
                    );
                  },
                },
              });
            },
          });
        }}
      />
      <Modal open={fieldDeleteError !== undefined} size="small">
        <Modal.Header>Unable to Delete Field</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            The following students are using this field:
            <ul>
              {fieldDeleteError
                ? fieldDeleteError.map((f, i) => <li key={i}>{f}</li>)
                : null}
            </ul>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button color="green" onClick={() => setFieldDeleteError(undefined)}>
            OK
          </Button>
        </Modal.Actions>
      </Modal>
    </List.Item>
  );
};

const EditFields = (props) => {
  const [fields, setFields] = useState(props.fields.slice());
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isAdding, setIsAdding] = useState(false);

  const removeField = (index) => {
    const newFields = fields.slice();
    newFields.splice(index, 1);
    setFields(newFields);
    setActiveIndex(-1);
  };

  const updateField = (index, field) => {
    const newFields = fields.slice();
    newFields[index] = field;
    setFields(newFields);
  };

  const addField = () => {
    const newFields = [
      {
        id: uuidv4(),
        control: "Input",
        label: "New Question",
        options: [],
        isNew: true,
      },
    ];

    setIsAdding(true);
    setFields([...fields, ...newFields]);
    setActiveIndex(fields.length);
  };

  const handleClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  };

  return (
    <Form>
      <Form.Field>
        <Accordion styled>
          {fields.map((field, i) => (
            <div key={`accordion${i}`}>
              <Accordion.Title
                active={activeIndex === i}
                index={i}
                onClick={handleClick}
              >
                <Icon name="dropdown" />
                {field.label}
              </Accordion.Title>
              <Accordion.Content active={activeIndex === i}>
                <EditField
                  initialField={field}
                  onDeleted={() => removeField(i)}
                  onCanceled={() => setIsAdding(false)}
                  onSaveCompleted={(field) => {
                    updateField(i, field);
                    setActiveIndex(-1);
                    setIsAdding(false);
                  }}
                />
              </Accordion.Content>
            </div>
          ))}
        </Accordion>
      </Form.Field>
      <Form.Group>
        <Form.Button basic type="button" onClick={props.onCancel}>
          Back
        </Form.Button>
        <Form.Button type="button" basic onClick={addField} disabled={isAdding}>
          <Button.Content>
            <Icon name="plus" /> Add Question
          </Button.Content>
        </Form.Button>
      </Form.Group>
    </Form>
  );
};

export default EditFields;
